import { useMemo, useState } from 'react';
import ReactQuill from 'react-quill';

import { EyeOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Form, Image, Input, notification, Upload } from 'antd';
import type { GetProp, UploadFile, UploadProps } from 'antd';
import { UploadFileStatus } from 'antd/es/upload/interface';

import { EventDetail, useEventDetail } from 'components/Pages/Admin/TabsMyEvents/context/EventDetail';
import { IEventPageEditV2 } from 'interfaces/event';
import eventService from 'services/event';
import { CDN_URL, BLINKET_EVENT_URL } from 'settings';
import { v4 } from 'uuid';

import customTemplateService from '../CustomTemplate/services';
import { PageContentV2 } from '../CustomTemplateV2/types';

const blinketUrl = BLINKET_EVENT_URL;

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const { Dragger } = Upload;

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });

interface ICustomSalePageFormProps {
  eventId: string;
  eventData: EventDetail;
  openPreview: () => void;
}

export const slugify = (text: string) => {
  return text
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/ /g, '-')
    .replace(/[^\w-]+/g, '');
};

export const CustomSalePageForm = ({ eventId, eventData, openPreview }: ICustomSalePageFormProps) => {
  const initialValues = useMemo(() => {
    if (!eventData) return null;

    const { pageContent, page_config } = eventData;
    const eventHasSalePage =
      eventId !== eventData?.advertisementLinks?.saleLink ||
      (eventId === eventData?.advertisementLinks?.saleLink && eventData?.event?.marketplace_active);

    if (!eventHasSalePage) {
      return {
        sale_url: slugify(eventData.event.title),
        headerimg: '',
        headerimgFileName: '',
        v2_desc: page_config.v2_desc,
        description: page_config.description,
        main_video_url: null,
        pixel_key: null
      };
    }

    const saleUrl = page_config.sale_url ?? eventData?.advertisementLinks?.saleLink ?? null;

    return {
      sale_url: eventHasSalePage ? saleUrl : slugify(eventData.event.title),
      headerimg: page_config.headerimg ?? null,
      headerimgFileName: page_config.headerimgFileName ?? null,
      v2_desc: page_config.v2_desc ?? pageContent.description,
      description: page_config.v2_desc ?? pageContent.description,
      main_video_url: page_config.main_video_url ?? null,
      pixel_key: page_config.pixel_key ?? null
    };
  }, [eventData, eventId]);

  const [form] = Form.useForm();
  const [description, setDescription] = useState({
    value: initialValues?.v2_desc,
    text: initialValues?.description,
    length: initialValues?.description?.length
  });
  const [fileList, setFileList] = useState<UploadFile | null>(
    eventData?.page_config?.headerimg
      ? {
          uid: v4(),
          name: initialValues?.headerimgFileName,
          status: 'done' as UploadFileStatus,
          url: eventData?.page_config?.headerimg
        }
      : null
  );
  const [previewImage, setPreviewImage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [draft, setDraft] = useState({ isDraft: false, saving: false });
  const [isModified, setIsModified] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const { setEventData } = useEventDetail();

  const checkSaleUrl = async (saleUrl: string): Promise<{ success: boolean; code?: 'string' }> => {
    return new Promise((resolve, reject) => {
      const draftUrl = eventData.event.sale_url;

      if (saleUrl === draftUrl) {
        setDraft({ ...draft, saving: false });
        return resolve({ success: true });
      }

      eventService.availableSaleUrl(saleUrl).subscribe({
        next: data => {
          setDraft({ ...draft, saving: false });
          if (!data) return resolve({ success: false });
          return resolve(data);
        },
        error: ({ data }) => {
          setDraft({ ...draft, saving: false });

          if (data?.code === 'ERR_CUSTOM' || data?.code === 'ERR_SPU409') {
            return reject(
              'A URL cadastrada para a página de vendas deste evento já existe. Por favor, informe uma nova URL para salvar.'
            );
          } else {
            return reject('URL inválida');
          }
        }
      });
    });
  };

  const handlePreview = async (file: UploadFile) => {
    if (fileList) {
      setPreviewImage(fileList.url as string);
      setPreviewOpen(true);
      return;
    }

    if (!file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.preview as string);
    setPreviewOpen(true);
  };

  const handleRemove = () => {
    form.setFieldValue('headerimg', '');
    form.setFieldValue('headerimgFileName', '');
    setFileList(null);
  };

  const beforeUpload = (file: FileType) => {
    if (!file.type.startsWith('image/')) {
      notification.error({ message: 'Apenas imagens são aceitas' });
      return Upload.LIST_IGNORE;
    }

    getBase64(file)
      .then(base64String => {
        const base64Img = base64String.split(',')[1];
        setFileList({
          uid: v4(),
          name: file.name,
          status: 'done' as UploadFileStatus,
          url: base64String
        });
        form.setFieldValue('headerimg', base64Img);
        form.setFieldValue('headerimgFileName', file.name);
        setPreviewImage(base64Img);
      })
      .catch(err => {
        console.error(err);
        return Upload.LIST_IGNORE;
      });

    return false;
  };

  const handleDraftPreview = () => {
    setDraft({ saving: true, isDraft: true });
    form.submit();
  };

  const onFinish = values => {
    if (descriptionError) return;

    const pageConfig: IEventPageEditV2 = {
      headerimg: values.headerimg || `${CDN_URL}${eventData.event.image_path}`,
      headerimgFileName: values.headerimgFileName ?? null,
      main_video_url: values.main_video_url ?? null,
      sale_url: values.sale_url,
      templateVersion: 'v3',
      v2_desc: description.value || null,
      draft_description: description.text || eventData.page_config.draft_description,
      description: description.text,
      pixel_key: values.pixel_key,
      external_keys: values.pixel_key
        ? {
            pixel_key: values.pixel_key
          }
        : null
    };
    const payload: PageContentV2 = {
      page_config: draft.isDraft ? eventData?.page_config : pageConfig,
      sale_url: values.sale_url,
      draft_description: description.text || null,
      description: description.text,
      draft_page_config: pageConfig
    };

    setDraft({ ...draft, saving: true });

    if (draft.isDraft) {
      setDraft({ saving: false, isDraft: false });
      openPreview();
      return;
    }

    const editData = {
      ...eventData,
      ...payload,
      salespage_active: true
    };
    customTemplateService.put(eventId, editData).subscribe({
      next: () => {
        eventService.allDetails(eventId).subscribe({
          next: updatedEventData => {
            setDraft({ saving: false, isDraft: false });
            setIsPublished(true);
            setIsModified(false);
            setEventData(updatedEventData);
            notification.success({ message: 'Página de vendas publicada com sucesso' });
            return;
          },
          error: () => {
            setDraft({ saving: false, isDraft: false });
            notification.error({ message: 'Erro ao salvar' });
          }
        });
      },
      error: ({ data: response }) => {
        setDraft({ saving: false, isDraft: false });

        if (response && response?.code === 'ERR_CUSTOM') {
          notification.error({ message: response.details });
        } else {
          notification.error({ message: 'Erro ao salvar' });
        }
      }
    });
  };

  const handleFormChange = () => {
    if (isPublished && !isModified) {
      setIsModified(true);
    }
  };

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={onFinish}
      onValuesChange={handleFormChange}
      layout='vertical'
      size='large'
    >
      <Form.Item label='Crie uma URL amigável para sua página de vendas:' required shouldUpdate>
        {() => {
          const hasError = form.getFieldError('sale_url').length > 0 && form.isFieldTouched('sale_url');

          return (
            <Form.Item
              name='sale_url'
              id='sale-url-item'
              validateDebounce={1000}
              rules={[
                () => ({
                  async validator(_, value) {
                    if (eventData.advertisementLinks.saleLink === value) {
                      return Promise.resolve();
                    }

                    if (!value) return Promise.reject('Campo obrigatório');

                    const formatedSaleUrl = slugify(value);
                    form.setFieldValue('sale_url', formatedSaleUrl);
                    if (value.length < 3) return Promise.reject('Mínimo 3 caracteres');

                    try {
                      const res = await checkSaleUrl(formatedSaleUrl);
                      if (!res) return Promise.reject('URL inválida');
                      if (res?.success === true) return Promise.resolve();
                    } catch (err) {
                      return Promise.reject(err);
                    }

                    return Promise.reject('Tente outra URL');
                  }
                })
              ]}
              validateStatus={hasError ? 'error' : ''}
              help={
                hasError
                  ? null
                  : 'Utilize palavras-chave curtas e de fácil digitação. Isso tornará o endereço mais fácil de memorizar e compartilhar.'
              }
            >
              <Input prefix={<div style={{ opacity: '0.7' }}>{blinketUrl + '/'}</div>} />
            </Form.Item>
          );
        }}
      </Form.Item>
      <Form.Item label='Imagem de capa da página de Vendas'>
        <Form.Item name='headerimg' noStyle>
          <Dragger
            id='headerimg-item'
            beforeUpload={beforeUpload}
            onPreview={handlePreview}
            customRequest={({ onSuccess }) => {
              onSuccess?.('ok');
            }}
            maxCount={1}
            listType='picture'
            accept='image/*'
            fileList={fileList ? [fileList] : []}
            onRemove={handleRemove}
          >
            <p className='ant-upload-drag-icon grey'>
              <UploadOutlined style={{ color: '#595959' }} />
            </p>
            <p className='ant-upload-text'>Clique ou arraste uma imagem para essa área</p>
            <p className='ant-upload-hint'>Tamanho recomendado: 1400px de altura por 730px de largura.</p>
            <p className='ant-upload-hint'>
              Se nenhuma imagem for enviada, utilizaremos automaticamente a imagem da capa cadastrada para este evento.
            </p>
          </Dragger>
        </Form.Item>
        {previewImage && (
          <Image
            wrapperStyle={{ display: 'none' }}
            preview={{
              visible: previewOpen,
              onVisibleChange: visible => setPreviewOpen(visible),
              afterOpenChange: visible => !visible && setPreviewImage(''),
              src: previewImage
            }}
            id='headerimg-preview'
          />
        )}
      </Form.Item>
      <Form.Item name='headerimgFileName' hidden />
      <Form.Item
        label='Conteúdo da página de vendas'
        required
        name='v2_desc'
        status={descriptionError ? 'error' : ''}
        validateStatus={descriptionError ? 'error' : ''}
        help={descriptionError ? 'Campo obrigatório' : ''}
        id='v2-desc-item'
      >
        <fieldset>
          <ReactQuill
            onChange={(value, d, s, editor) => {
              if (editor.getLength() - 1 <= 0) {
                setDescriptionError(true);
                setDescription({
                  value,
                  text: editor.getText(),
                  length: editor.getLength() - 1
                });
                return;
              }

              setDescriptionError(false);

              if (editor.getLength() - 1 > 2000) {
                return setDescription({ ...description });
              }

              setDescription({
                value,
                text: editor.getText(),
                length: editor.getLength() - 1
              });

              form.setFieldsValue({ v2_desc: value });

              handleFormChange();
            }}
            placeholder='Digite aqui sua programação'
            style={{ wordBreak: 'break-all', maxHeight: '200px', overflowY: 'scroll' }}
            value={description.value}
            id='v2-desc-input'
          />
          <p style={{ color: '#595959' }}>{description.length}/2000</p>
        </fieldset>
      </Form.Item>
      <Form.Item shouldUpdate>
        {() => {
          const hasError = form.getFieldError('main_video_url').length > 0 && form.isFieldTouched('main_video_url');

          return (
            <Form.Item
              label='Vídeo de apresentação do evento'
              name='main_video_url'
              id='main-video-url-item'
              rules={[{ type: 'url', message: 'URL inválida' }]}
              validateStatus={hasError ? 'error' : ''}
              help={
                hasError
                  ? null
                  : 'Você pode cadastrar apenas vídeos do YouTube ou Vimeo. Basta copiar a URL do vídeo e colá-la neste campo.'
              }
            >
              <Input id='main-video-url-input' placeholder='Cole aqui a URL do Vídeo' />
            </Form.Item>
          );
        }}
      </Form.Item>

      <Form.Item
        label='Facebook Pixel ID (Meta)'
        name='pixel_key'
        id='pixel-key-item'
        extra={
          'Configurar o Facebook Pixel permite monitorar as ações dos visitantes em sua página, otimizando campanhas publicitárias.'
        }
      >
        <Input id='pixel-key-input' placeholder='Cole aqui seu ID' />
      </Form.Item>
      <div style={{ display: 'flex', justifyContent: 'right', gap: '10px', padding: '12px 50px' }}>
        <Button
          icon={<EyeOutlined />}
          loading={draft.saving}
          onClick={handleDraftPreview}
          size='large'
          id='preview-button'
          disabled={!isPublished || isModified}
        >
          Visualizar
        </Button>
        <Button type='primary' htmlType='submit' disabled={draft.saving} size='large' id='save-button'>
          Publicar
        </Button>
      </div>
    </Form>
  );
};
